import React, { useContext, useEffect, useState } from 'react'
import { useTheme } from 'emotion-theming'
import { makeStyles } from '@material-ui/core'
import Hidden from '@material-ui/core/Hidden'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import { useLocation } from '@reach/router'

// Import Components
import { Button, Link } from '@jelly/ui'
import { EmailSubscribeContext } from '../EmailSubscribeProvider'
import { Search } from '../svgs/Search'
import { SearchPanel } from '../Search/SearchPanel'
import { SiteConfigContext } from '../SiteConfigProvider'
import { SubHubHamburgerMenu } from '../HamburgerMenu/SubHubHamburgerMenu'
import { SubHubMenu } from './SubHubMenu'
import { trackNavEvent } from '../HeaderBanner'

const useStyles = ({ isMenuPanelOpen }) =>
	makeStyles(
		(theme) => ({
			AdvertiseWithUsButton: {
				height: '36px',
				marginRight: '1rem',
				padding: '0 1rem',
				width: 'auto',
			},
			ElementIcon: {
				margin: 'auto',
			},
			Form: {
				marginRight: '0',
				maxWidth: '70%',

				[theme.breakpoints.up('md')]: {
					marginRight: '1.5rem',
					maxWidth: '100%',
				},
			},
			Hamburger: {
				display: 'flex',
				flexDirection: 'column',
				height: '40%',
				justifyContent: 'space-between',
				margin: 'auto',
				position: 'relative',
				width: '35px',
			},
			HamburgerWrapper: {
				border: '0',
				cursor: 'pointer',
				display: 'inline-block',
				background: theme.palette.common.white,
				boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.12)',
				height: '45px',
				position: 'relative',
				verticalAlign: 'top',
				minWidth: '65px',
				zIndex: '1000',
				width: '100%',
				[theme.breakpoints.up('sm')]: {
					height: '55px',
					width: '80px',
				},
			},
			HeaderBannerWrapper: {
				display: 'flex',
				height: '100%',
				margin: 'auto',
				maxWidth: theme.breakpoints.values.xl,
				position: 'relative',
				width: '100%',
			},
			HubMenuContainer: {
				display: 'flex',
				justifyContent: 'space-between', // please leave this duplicate FIRST for ie11 support
				justifyContent: 'space-evenly', // eslint-disable-line
				margin: 'auto',
				maxWidth: theme.breakpoints.values.xl,
				textAlign: 'center',
				width: '99%',
				[theme.breakpoints.up('xl')]: {
					width: '100%',
				},
			},
			IconContainer: {
				alignItems: 'center',
				display: 'flex',
				justifyContent: 'flex-end',
				margin: 'auto 0 auto auto',
				right: '0',
				zIndex: '4', //to keep when the hamburger menu is open
				[theme.breakpoints.up('sm')]: {
					zIndex: '1',
				},
				[theme.breakpoints.up('max')]: {
					'& li:last-child': {
						marginRight: '0',
					},
				},
			},
			JoinButton: {
				height: '36px',
				margin: '0 1rem',
				padding: '0 1rem',
				width: 'auto',
			},
			Layer: {
				background: theme.palette.secondary.main,
				display: 'block',
				height: '2px',
				position: 'relative',
				transformOrigin: '4px 0px',
				transition: `transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1), background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
				opacity 0.55s ease`,
				'&:nth-of-type(1)': {
					transform: isMenuPanelOpen ? 'rotate(45deg) translate(0, -5px)' : 'rotate(0deg)',
					[theme.breakpoints.up('sm')]: {
						transform: isMenuPanelOpen ? 'rotate(45deg) translate(2px, -2px)' : 'rotate(0deg)',
					},
				},
				'&:nth-of-type(2)': {
					display: isMenuPanelOpen ? 'none' : 'block',
					transition: 'all 1s ease',
				},
				'&:nth-of-type(3)': {
					transform: isMenuPanelOpen ? 'rotate(-45deg) translate(1px, 1px)' : 'rotate(0deg)',
				},
			},
			LeftSideContainer: {
				display: 'flex',
				paddingTop: '0.5rem',
				[theme.breakpoints.up('lg')]: {
					paddingLeft: '0.5rem',
				},
				[theme.breakpoints.up('xl')]: {
					paddingLeft: '0',
				},
			},
			LogoContainer: {
				zIndex: '1001',
				[theme.breakpoints.up('xl')]: {
					zIndex: '1',
				},
			},
			NavigationBar: {
				background: theme.palette.tertiary.main,
				width: '100%',
			},
			Overlay: {
				background: theme.palette.primary.main,
				height: '100vh',
				position: 'absolute',
				top: '0',
				opacity: '0.85',
				transition: 'all 0.2s ease',
				visibility: isMenuPanelOpen ? 'visible' : 'hidden',
				width: '100%',
				/* This is set to 3 because the MBP meatball menu has a z-index of 2 and we want to overlap that when the hamburger menu is open */
				zIndex: 3,
			},
			SearchIconWrapper: {
				cursor: 'pointer',
				height: '20px',
				margin: '2px 1rem 0 0',
				width: '20px',

				[theme.breakpoints.up('xs')]: {
					marginRight: '1.5rem',
				},
				'&:hover': {
					'& path, line': {
						stroke: theme.palette.secondary.main,
					},
				},
			},
			StyledInputAdorment: {
				height: '1.25rem',
				width: '1.25rem',
			},
			StyledTextField: {
				'& fieldset': {
					borderRadius: '0',
					margin: 'auto',
					minHeight: '40px',
				},
			},
		}),
		{ name: 'subHubHeader' }
	)

/**
 * This is the Sub Hub Header Banner App Component
 *
 * Open and Close States:
 * 	- Solo Search Panel
 * 	- Solo Account Panel
 * 	- Search Panel from Hamburger Menu
 * 	- Account Panel from Hamburger Menu
 * 	- Account Panel within Tablet
 */
export function SubHubHeader() {
	const {
		siteComponents: { HeaderLogo },
		siteConstants: {
			EMAIL_CAPTURE_SOURCES: { subscribeButton },
			HUB_LINKS,
			SHOULD_SHOW_ADVERTISE_WITH_US_BUTTON,
		},
	} = useContext(SiteConfigContext)

	const { emailSubscribeFormSubmitted, setEmailSubscribeSource, setIsVisible } = useContext(EmailSubscribeContext)

	const {
		MATCH_MEDIA,
		search: { strokeColor },
	} = useTheme()

	const { pathname } = useLocation()

	const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false)
	const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false)
	const [isAccountPanelOpen, setIsAccountPanelOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('small business')

	const isMenuPanelOpen = isAccountPanelOpen || isSearchPanelOpen || isHamburgerMenuOpen

	useEffect(() => {
		if (isMenuPanelOpen) {
			window.document.body.style.overflow = 'hidden'
			window.document.body.style.position = 'fixed'
		} else {
			window.document.body.style.overflow = 'visible'
			window.document.body.style.position = ''
		}
	}, [isMenuPanelOpen])

	const handleEscPanel = (event) => {
		if (event.keyCode === 27) {
			return isSearchPanelOpen
				? setIsSearchPanelOpen(false)
				: isAccountPanelOpen
				? setIsAccountPanelOpen(false)
				: setIsHamburgerMenuOpen(false)
		}
	}

	useEffect(() => {
		document.addEventListener('keydown', handleEscPanel)

		return () => {
			document.removeEventListener('keydown', handleEscPanel)
		}
	})

	const handleMenuStatus = (event, { menuType = 'hamburger' }) => {
		event.preventDefault()
		const isMobile = !window.matchMedia(MATCH_MEDIA.S).matches
		switch (menuType) {
			case 'account panel':
				isMobile && setIsAccountPanelOpen(!isAccountPanelOpen)
				break
			case 'search panel':
				setIsSearchPanelOpen(!isSearchPanelOpen)
				break
			case 'hamburger':
			default:
				if (isAccountPanelOpen) {
					setIsAccountPanelOpen(!isAccountPanelOpen)
					isHamburgerMenuOpen && setIsHamburgerMenuOpen(false)
				} else if (isSearchPanelOpen) {
					setIsSearchPanelOpen(!isSearchPanelOpen)
					isHamburgerMenuOpen && setIsHamburgerMenuOpen(false)
				} else {
					setIsHamburgerMenuOpen(!isHamburgerMenuOpen)
				}
		}
	}

	const handleSubscribeClick = (e, slug) => {
		e.preventDefault()
		trackNavEvent({ destination: slug, type: 'Subscribe' })
	}

	const handleAdvertiseWithUsClick = (slug) => {
		trackNavEvent({ destination: slug, type: 'Header AdvertiseWithUs link' })
	}

	const handleDesktopSearchSubmit = (event) => {
		event.preventDefault()

		window.location.href = `/search/?q=${searchTerm}`
	}

	const classes = useStyles({ isMenuPanelOpen, isHamburgerMenuOpen, isSearchPanelOpen })()

	return (
		<>
			{/* NAVY OVERLAY ON REST OF THE SITE */}
			<div className={classes.Overlay} onClick={(e) => handleMenuStatus(e, { menuType: 'hamburger' })} />
			<SearchPanel
				handleClosePanel={(e) => handleMenuStatus(e, { menuType: 'search panel' })}
				isSearchVisible={isSearchPanelOpen}
				showBackToMenu={isHamburgerMenuOpen}
			/>
			<nav className={classes.HeaderBannerWrapper} role="navigation">
				<div className={classes.LeftSideContainer}>
					<Hidden lgUp initialWidth="xs" implementation="css">
						<button
							aria-expanded={isHamburgerMenuOpen}
							aria-label="hamburger menu"
							className={classes.HamburgerWrapper}
							onClick={(e) => handleMenuStatus(e, { menuType: 'hamburger' })}
						>
							<div className={classes.Hamburger}>
								<span className={classes.Layer} />
								<span className={classes.Layer} />
								<span className={classes.Layer} />
							</div>
						</button>
						<SubHubHamburgerMenu
							handleMenuStatus={handleMenuStatus}
							handleSearchClick={(e) => handleMenuStatus(e, { menuType: 'search panel' })}
							isHamburgerMenuOpen={isHamburgerMenuOpen}
						/>
					</Hidden>
					<div className={classes.LogoContainer}>
						<HeaderLogo />
					</div>
				</div>

				{/* Search Icon + User Navigation Bubble */}
				<div className={classes.IconContainer}>
					{!emailSubscribeFormSubmitted && (
						<Hidden lgDown initialWidth="xs" implementation="css">
							<Button
								className={classes.JoinButton}
								aria-label="Subscribe"
								onClick={(e) => {
									handleSubscribeClick(e, window.location.href)
									setIsHamburgerMenuOpen(false)
									setIsVisible(true)
									setEmailSubscribeSource(subscribeButton)
								}}
							>
								Subscribe
							</Button>
						</Hidden>
					)}
					<>
						{SHOULD_SHOW_ADVERTISE_WITH_US_BUTTON && pathname !== '/connect' && (
							<Hidden lgDown initialWidth="xs" implementation="css">
								<Button
									aria-label="Advertise With Us"
									as={Link}
									className={classes.AdvertiseWithUsButton}
									href="/connect"
									onClick={() => handleAdvertiseWithUsClick(window.location.href)}
									variant="secondary"
								>
									Advertise With Us
								</Button>
							</Hidden>
						)}
						<Hidden only={['xs']}>
							<form className={classes.Form} noValidate onSubmit={handleDesktopSearchSubmit}>
								<label htmlFor="search" hidden>
									Search:
								</label>
								<TextField
									className={classes.StyledTextField}
									id="search"
									InputProps={{
										startAdornment: (
											<InputAdornment className={classes.StyledInputAdorment} position="start">
												<Search strokeColor={strokeColor} />
											</InputAdornment>
										),
									}}
									aria-label="search"
									placeholder="Search"
									onInput={(e) => setSearchTerm(e.target.value)}
									variant="outlined"
								/>
							</form>
						</Hidden>
						<Hidden smUp initialWidth="xs" implementation="css">
							<div
								className={classes.SearchIconWrapper}
								onClick={(e) => handleMenuStatus(e, { menuType: 'search panel' })}
							>
								<Search strokeColor={strokeColor} />
							</div>
						</Hidden>
					</>
				</div>
			</nav>

			{/* Sub Hub Navigation Links */}
			<Hidden mdDown initialWidth="xs" implementation="css">
				<nav className={classes.NavigationBar} role="menubar">
					<div className={classes.HubMenuContainer}>
						{HUB_LINKS.map((link, i) => {
							return (
								<React.Fragment key={`hub-element-${i}`}>
									<SubHubMenu
										featuredTopics={link.featuredTopics}
										slug={link.slug}
										subHubs={link.subHubs}
										title={link.title}
									/>
								</React.Fragment>
							)
						})}
					</div>
				</nav>
			</Hidden>
		</>
	)
}
export default SubHubHeader
